import { BasicButton, SecondCustomInput, SelectBase } from "@/components";
import useProfile from "@/hooks/useProfile";
import { STORAGE_KEYS } from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import { postRegister } from "@/redux/reducers/auth";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import OutlinedFlagRoundedIcon from "@mui/icons-material/OutlinedFlagRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PortraitRoundedIcon from "@mui/icons-material/PortraitRounded";
import { createFileRoute, redirect } from "@tanstack/react-router";
import CountryList from "country-list-with-dial-code-and-flag";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export const Route = createFileRoute(
	"/create-account/professional-credentials",
)({
	beforeLoad: () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/create-account",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	const { profile } = useProfile();
	const countries = CountryList.getAll();
	// initials form credentials
	const [formCredentials, setFormCredentials] = useReducer(
		(state: any, newState: any) => ({ ...state, ...newState }),
		{
			country: "Malaysia",
			profession: "",
			name: "",
			workplace: "",
			registration_number: "",
			referral_code: "",
		},
	);
	const [alertMessage, setAlertMessage] = useState({
		registration_number: "",
	});
	const message = useSelector((state: any) => state.auth?.postRegister);
	// hospitals data from api
	// const hospitalsData = useSelector((state: any) => state.content?.hospitals?.data)
	// button disable
	const [buttonDisable, setButtonDisable] = useState(true);
	const [loading, setLoading] = useState(false);

	// setFormCredentials from localStorage
	useEffect(() => {
		const savedFormCredentials = localStorage.getItem(
			STORAGE_KEYS.FORM_CREDENTIALS,
		);
		if (savedFormCredentials) {
			setFormCredentials(JSON.parse(savedFormCredentials));
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (profile) {
			setFormCredentials({
				country: profile.country ?? formCredentials.country,
				profession: profile.profession,
				name: profile.fullname,
				workplace: profile.hospital?.name,
				registration_number: profile.registration_number,
				referral_code: profile.referral_code,
			});
		}
	}, [profile]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		//get hospitals data
		// dispatch(getHospitals({ page: 1, limit: 10 }))
		// check input value
		setButtonDisable(
			!(
				formCredentials.name &&
				formCredentials.name &&
				formCredentials.workplace &&
				formCredentials.registration_number
			),
		);
		if (message?.status === "failed") {
			setLoading(false);
			if (message?.error) {
				setAlertMessage((prevState) => ({
					...prevState,
					registration_number: message?.error?.messages,
				}));
			}
		}
	}, [dispatch, formCredentials, buttonDisable, message]);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		// Split the name into first_name and last_name
		const nameParts = formCredentials.name.split(" ");
		const first_name = nameParts[0];
		const last_name = nameParts.slice(1).join(" ");
		dispatch(
			postRegister({
				id: profile?.id,
				first_name: first_name,
				country: formCredentials.country,
				profession: formCredentials.profession,
				last_name: last_name,
				hospital_name: formCredentials.workplace,
				registration_number: formCredentials.registration_number,
				referral_code: formCredentials.referral_code,
				callback: () => {
					setAlertMessage((prevState) => ({
						...prevState,
						registration_number: "",
					}));

					localStorage.setItem(
						STORAGE_KEYS.FORM_CREDENTIALS,
						JSON.stringify(formCredentials),
					);
					return navigate({
						to: "/create-account/professional-profile",
					}).then(() => {
						setLoading(false);
					});
				},
			}),
		);
	}, [formCredentials, profile, dispatch, navigate]);

	return (
		<>
			<div className="relative container flex-1 overflow-y-auto">
				<div className="py-4 space-y-6">
					<SelectBase
						placeholder="Country"
						name="country"
						label="Country"
						value={formCredentials.country}
						onChange={(e) =>
							e ? setFormCredentials({ country: e }) : undefined
						}
						startIcon={<OutlinedFlagRoundedIcon />}
						options={countries}
					/>
					<SelectBase
						placeholder="Profession"
						name="profession"
						label="Profession"
						value={formCredentials.profession}
						onChange={(e) => setFormCredentials({ profession: e })}
						startIcon={<PortraitRoundedIcon />}
						options={[
							{ id: "1", name: "Doctor" },
							{ id: "2", name: "Nurse" },
							{ id: "3", name: "Midwife" },
							{ id: "4", name: "Dentist" },
							{ id: "5", name: "Occupational Therapist" },
							{ id: "6", name: "Physiotherapist" },
							{ id: "7", name: "Pharmacist" },
							{ id: "8", name: "Psychologist" },
							{ id: "9", name: "Social Worker" },
							{ id: "10", name: "Admin Staff" },
							{ id: "11", name: "Medical Student" },
							{ id: "12", name: "Other Health Care Professionals" },
						]}
					/>
					<SecondCustomInput
						type="text"
						label="Full Name"
						name="name"
						value={formCredentials.name}
						onChange={(e) => setFormCredentials({ name: e.target.value })}
						startIcon={<PersonOutlineOutlinedIcon />}
					/>
					<SecondCustomInput
						type="text"
						label="Workplace"
						name="workplace"
						value={formCredentials.workplace}
						onChange={(e) => setFormCredentials({ workplace: e.target.value })}
						startIcon={<LocalHospitalOutlinedIcon />}
					/>
					<SecondCustomInput
						type="text"
						label="Professional Registration/Passport/ID No."
						name="registration"
						errorMessage={alertMessage.registration_number}
						value={formCredentials.registration_number}
						onChange={(e) =>
							setFormCredentials({ registration_number: e.target.value })
						}
						startIcon={<BadgeOutlinedIcon />}
					/>
					<SecondCustomInput
						type="text"
						label="Referral Code (Optional)"
						name="refferal-code"
						value={formCredentials.referral_code}
						onChange={(e) =>
							setFormCredentials({ referral_code: e.target.value })
						}
						startIcon={<BadgeOutlinedIcon />}
					/>
				</div>
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					variant="contained"
					color="secondary"
					onClick={handleSubmit}
					disabled={buttonDisable}
					loading={loading}
					className="w-full !h-12 !text-sm font-semibold !shadow-none"
				>
					Next
				</BasicButton>
			</div>
		</>
	);
}
